import { CommandsManager, Enums, log, ServicesManager } from '@core';
import { DisplaySet, HangingProtocolService, StudyMetadata } from '@core/types';
import { sortingCriteria } from '@core/utils/sortStudy';
import getStudies from './studiesList';

/// Làm hàm generic cho phép auto bật mode cho một số modality cụ thể
const SPECIALIZED_PROTOCOL_MAP: Record<string, string> = {
	MG: 'mammo',
	NM: 'nuclearMed',
	PT: 'nuclearMed',
};

const LOAD_FULL_SERIES_MODALITIES = Object.keys(SPECIALIZED_PROTOCOL_MAP);

/**
 * HÀM GỌI API, XỬ LÝ LẤY STUDY, SERIES METADATA
 * dùng trong:  defaultRouteInit (lần đâu mở viewer), tính năng patient history (mở thêm các studies mới trên viewer)
 */
export const getNewStudies = async ({
	servicesManager,
	studyInstanceUIDs,
	commandsManager,
	dataSource,
	hangingProtocolId,
	stageIndex,
}: {
	servicesManager: ServicesManager;
	studyInstanceUIDs: string[];
	commandsManager: CommandsManager;
	dataSource;
	hangingProtocolId: string;
	stageIndex?: number;
}) => {
	const { customizationService, hangingProtocolService, displaySetService } = servicesManager.services;
	function applyHangingProtocol() {
		const displaySets = displaySetService.getActiveDisplaySets();
		if (!displaySets || !displaySets.length) {
			return;
		}

		// Gets the studies list to use
		const studies = getStudies(studyInstanceUIDs, displaySets);
		// study being displayed, and is thus the "active" study.
		const activeStudy = studies[0];
		const isSpecializedMode = runSpecializedProtocol(activeStudy, studies, displaySets, hangingProtocolService);
		// run the hanging protocol matching on the displaySets with the predefined
		// hanging protocol in the mode configuration
		if (!isSpecializedMode) {
			hangingProtocolService.run({ studies, activeStudy, displaySets }, hangingProtocolId, {
				stageIndex,
			});
		}
	}
	log.time(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
	log.time(Enums.TimingEnum.STUDY_TO_FIRST_IMAGE);
	const allRetrieves = studyInstanceUIDs.map((StudyInstanceUID) =>
		dataSource.retrieve.series.metadata({
			StudyInstanceUID,
			returnPromises: true,
			sortCriteria: customizationService.get('sortingCriteria') || sortingCriteria.seriesSortCriteria.seriesInfoSortingCriteria,
		}),
	);
	await Promise.allSettled(allRetrieves).then(async (promises) => {
		const rejectedPromises = promises.filter((promise) => promise.status === 'rejected');
		if (rejectedPromises.length !== 0) {
			console.error('rejectedPromises', rejectedPromises);
			commandsManager.run({ commandName: 'showCannotViewImagesPopup' });
			return;
		}

		log.timeEnd(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
		log.time(Enums.TimingEnum.DISPLAY_SETS_TO_FIRST_IMAGE);
		log.time(Enums.TimingEnum.DISPLAY_SETS_TO_ALL_IMAGES);
		const allPromises = [];
		const remainingPromises = [];

		function startRemainingPromises(remainingPromises) {
			remainingPromises.forEach((p) => p.forEach((p) => p.start()));
		}

		promises.forEach((promise) => {
			if (promise.status === 'fulfilled') {
				const retrieveSeriesMetadataPromise = promise.value;
				if (Array.isArray(retrieveSeriesMetadataPromise)) {
					const modality = retrieveSeriesMetadataPromise[0]?.metadata?.Modality || retrieveSeriesMetadataPromise[0]?.Modality;
					const isLoadFullSeries = LOAD_FULL_SERIES_MODALITIES.includes(modality);
					const { requiredSeries, remaining } = hangingProtocolService.filterSeriesRequiredForRun(
						hangingProtocolId,
						retrieveSeriesMetadataPromise,
						isLoadFullSeries,
					);
					const requiredSeriesPromises = requiredSeries
						.filter((promise) => typeof promise.start === 'function')
						.map((promise) => promise.start());
					if (requiredSeriesPromises.length > 0) {
						allPromises.push(Promise.allSettled(requiredSeriesPromises));
						remainingPromises.push(remaining);
					}
				}
			}
		});
		if (allPromises.length === 0) {
			// If no series were retrieved, we can apply the hanging protocol immediately
			applyHangingProtocol();
			return;
		}
		await Promise.allSettled(allPromises).then(applyHangingProtocol);
		// DÙNG TIMEOUT VÌ CHƯA CÓ CÁCH XỬ LÝ KHÁC,
		// MỤC ĐÍCH LÀ ĐỢI SERIES METADATA, FRAME CỦA SERIES ĐẦU TIÊN LOAD,HIỂN THỊ XONG https://viewer2.atlassian.net/jira/software/projects/V2D/boards/1?selectedIssue=V2D-321
		// MỚI LOAD ĐẾN CÁC SERIES METADATA CÒN LẠI
		setTimeout(async () => await startRemainingPromises(remainingPromises), 500);
	});
};

const runSpecializedProtocol = (
	activeStudy: StudyMetadata,
	studies: StudyMetadata[],
	displaySets: DisplaySet[],
	hangingProtocolService: HangingProtocolService,
): boolean => {
	const modalities = activeStudy.ModalitiesInStudy || [];

	const matchedModality = modalities.find((m) => SPECIALIZED_PROTOCOL_MAP[m]);

	if (matchedModality) {
		const protocolId = SPECIALIZED_PROTOCOL_MAP[matchedModality];
		hangingProtocolService.run({ studies, activeStudy, displaySets }, protocolId);
		return true;
	}
	return false;
};
